import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  Container,
  Header,
  Modal,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  InvestigationEvent,
  InvestigationTimelinePage,
  Stage,
} from "../../lusca-search-api/generated-src";
import { InvestigationsApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import {
  InvestigationApiError,
  useAddComment,
  useDeleteComment,
  useEditComment,
} from "../../hooks/useInvestigations";
import {
  formatTimestamp,
  isTerminal,
  submitOnModEnter,
} from "./investigationUtils";
import {
  CommentEditor,
  DetailText,
  commentAttachments,
  commentText,
} from "./InvestigationTimeline";
import { CommentInput } from "./CommentInput";
import "./CommentMarkdown.scss";
import { OriginBadge } from "./InvestigationLinkList";
import { UserAvatar } from "./UserAvatar";
import { useAttachmentUploads } from "../../hooks/useAttachmentUploads";
import { useCommentDraft } from "../../hooks/useCommentDraft";
import { AttachmentDropzone } from "./attachments/AttachmentDropzone";
import { AttachmentList } from "./attachments/AttachmentList";
import {
  InlineAttachmentProvider,
  useInlineAttachments,
  withoutInline,
} from "./attachments/inlineAttachments";

interface InvestigationCommentsProps {
  stage: Stage;
  investigationId: string;
  currentUser: string | null;
  owner?: string;
  /** Investigation status; resolved/cancelled cases are closed to new or edited comments. */
  status?: string | null;
  /** Newest-first timeline page the investigation detail already carries. */
  initialEvents: InvestigationEvent[];
  initialNextToken?: string;
  onError: (message: string) => void;
}

/** Pages fetched per "Load older comments" click before giving up for this click. */
const MAX_PAGES_PER_LOAD = 5;

const isComment = (event: InvestigationEvent) => event.type === "COMMENT";

/**
 * General comments: the free-text progress notes people post on an investigation, shown
 * where they are written instead of only inside the audit log. Comments are still timeline
 * events (type COMMENT), so this is a filtered view of the same immutable trail. This view
 * shows what each comment says now (commentText); the Audit log tab shows what was posted and
 * every edit or deletion as its own old → new entry.
 */
export const InvestigationComments: React.FC<InvestigationCommentsProps> = ({
  stage,
  investigationId,
  currentUser,
  owner,
  status,
  initialEvents,
  initialNextToken,
  onError,
}) => {
  const addComment = useAddComment(stage);
  const editComment = useEditComment(stage);
  const deleteComment = useDeleteComment(stage);

  // Persisted so switching to another tab (which unmounts this one), refreshing or closing the
  // browser tab doesn't lose an unposted comment.
  const {
    text: draft,
    setText: setDraft,
    initialAttachments: draftAttachments,
    setAttachments: setDraftAttachments,
    clear: clearDraft,
    restored,
  } = useCommentDraft(stage, investigationId);
  // "Clear draft" is only offered for a draft you came back to, not one you're typing now.
  const [showClear, setShowClear] = useState(restored);
  const uploads = useAttachmentUploads(
    stage,
    investigationId,
    draftAttachments
  );
  const insertInline = useInlineAttachments(uploads, draft, setDraft);
  const readyAttachments = uploads.items
    .filter((item) => item.status === "ready" && item.key)
    .map((item) => ({
      key: item.key!,
      fileName: item.fileName,
      contentType: item.contentType,
      size: item.size,
    }));
  const readyKeys = readyAttachments.map((a) => a.key).join("|");
  useEffect(() => {
    setDraftAttachments(readyAttachments);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the ready set
  }, [readyKeys, setDraftAttachments]);
  const canPost =
    (!!draft.trim() || uploads.refs.length > 0) && !uploads.uploading;
  const [extra, setExtra] = useState<InvestigationEvent[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(
    initialNextToken
  );
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InvestigationEvent | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  // The detail query is invalidated after every comment mutation, so initialEvents refreshes
  // on its own; only pages we fetched ourselves need de-duplicating against it.
  const initialIds = new Set(initialEvents.map((event) => event.eventId));
  const comments = [
    ...initialEvents,
    ...extra.filter((event) => !initialIds.has(event.eventId)),
  ].filter(isComment);

  const closed = isTerminal(status);

  const canManage = (event: InvestigationEvent) =>
    !closed &&
    !event.deletedAt &&
    !!currentUser &&
    (currentUser === event.actor || currentUser === owner);

  const submit = () =>
    addComment
      .mutateAsync({
        investigationId,
        request: {
          text: draft.trim(),
          attachments: uploads.refs.length ? uploads.refs : undefined,
        },
      })
      .then(() => {
        clearDraft();
        uploads.reset();
        setShowClear(false);
      })
      .catch((err: InvestigationApiError) => onError(err.message));

  const patchExtra = (updated: InvestigationEvent | undefined) => {
    if (!updated?.eventId) return;
    setExtra((prev) =>
      prev.map((event) => (event.eventId === updated.eventId ? updated : event))
    );
  };

  const saveEdit = async (
    event: InvestigationEvent,
    text: string,
    attachments?: { key: string }[]
  ) => {
    if (!event.eventId) return;
    setError(null);
    try {
      patchExtra(
        await editComment.mutateAsync({
          investigationId,
          eventId: event.eventId,
          text,
          attachments,
        })
      );
      setEditingId(null);
    } catch (e) {
      setError((e as InvestigationApiError).message || "Request failed");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.eventId) return;
    setError(null);
    try {
      patchExtra(
        await deleteComment.mutateAsync({
          investigationId,
          eventId: deleteTarget.eventId,
        })
      );
      setDeleteTarget(null);
    } catch (e) {
      setError((e as InvestigationApiError).message || "Request failed");
    }
  };

  /** Keep paging the timeline until at least one more comment shows up or it runs out. */
  const loadOlder = async () => {
    if (!nextToken) return;
    setLoadingOlder(true);
    const fetchPage = async (
      token: string,
      pagesLeft: number
    ): Promise<string | undefined> => {
      const page: InvestigationTimelinePage = (
        await InvestigationsApiFactory().getInvestigationTimeline(
          stage,
          investigationId,
          50,
          token
        )
      ).data;
      const events = page.events || [];
      setExtra((prev) => [...prev, ...events]);
      if (!page.nextToken || pagesLeft <= 1 || events.some(isComment)) {
        return page.nextToken;
      }
      return fetchPage(page.nextToken, pagesLeft - 1);
    };
    try {
      setNextToken(await fetchPage(nextToken, MAX_PAGES_PER_LOAD));
    } catch (e) {
      setError((e as InvestigationApiError).message || "Request failed");
    } finally {
      setLoadingOlder(false);
    }
  };

  const renderBody = (event: InvestigationEvent) => {
    if (event.deletedAt) {
      return (
        <Box color="text-body-secondary" fontSize="body-s">
          <i>
            Comment deleted by {event.deletedBy || "unknown"} ·{" "}
            {formatTimestamp(event.deletedAt)}
          </i>
        </Box>
      );
    }
    if (event.eventId === editingId) {
      return (
        <CommentEditor
          stage={stage}
          investigationId={investigationId}
          initialText={commentText(event)}
          initialAttachments={commentAttachments(event)}
          saving={editComment.isLoading}
          onCancel={() => setEditingId(null)}
          onSave={(text, attachments) => {
            saveEdit(event, text, attachments).catch(console.error);
          }}
        />
      );
    }
    const text = commentText(event);
    const attachments = commentAttachments(event);
    return (
      <SpaceBetween size="xxs">
        {text && (
          <InlineAttachmentProvider
            stage={stage}
            investigationId={investigationId}
            attachments={attachments}
          >
            <DetailText text={text} markdown mentions={event.mentions} />
          </InlineAttachmentProvider>
        )}
        <AttachmentList
          stage={stage}
          investigationId={investigationId}
          attachments={withoutInline(attachments, text)}
        />
        {event.editedAt && (
          <Box color="text-body-secondary" fontSize="body-s">
            edited by {event.editedBy || "unknown"} ·{" "}
            {formatTimestamp(event.editedAt)} · original in the audit log
          </Box>
        )}
      </SpaceBetween>
    );
  };

  return (
    <Container
      header={
        <Header
          variant="h3"
          counter={`(${comments.length}${nextToken ? "+" : ""})`}
        >
          Comments
        </Header>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {closed ? (
          <Box color="text-body-secondary" fontSize="body-s">
            This investigation is{" "}
            {status === "WITHDRAWN" ? "cancelled" : "resolved"}; comments are
            closed.
          </Box>
        ) : (
          <SpaceBetween size="xs">
            <AttachmentDropzone
              uploads={uploads}
              disabled={addComment.isLoading}
              onFilesAdded={insertInline}
            >
              <InlineAttachmentProvider pending={uploads.items}>
                <CommentInput
                  value={draft}
                  onChange={setDraft}
                  onKeyDown={submitOnModEnter(() => {
                    submit().catch(console.error);
                  }, !canPost || addComment.isLoading)}
                  placeholder="What changed, what you tried, what you need… (⌘/Ctrl+Enter to post)"
                  rows={2}
                  ariaLabel="Comment"
                  stage={stage}
                  currentUser={currentUser}
                />
              </InlineAttachmentProvider>
            </AttachmentDropzone>
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                {showClear && (!!draft || uploads.items.length > 0) && (
                  <Button
                    variant="link"
                    disabled={addComment.isLoading}
                    onClick={() => {
                      clearDraft();
                      uploads.reset();
                      setShowClear(false);
                    }}
                  >
                    Clear draft
                  </Button>
                )}
                <Button
                  variant="primary"
                  disabled={!canPost}
                  loading={addComment.isLoading}
                  onClick={() => {
                    submit().catch(console.error);
                  }}
                >
                  Add comment
                </Button>
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        )}

        {comments.length === 0 ? (
          <Box color="text-status-inactive" fontSize="body-s">
            No comments yet.
          </Box>
        ) : (
          <SpaceBetween size="s">
            {comments.map((event) => (
              <div
                key={event.eventId}
                data-testid="comment-row"
                className="comment-card"
              >
                <div className="comment-card__header">
                  {event.actor && <UserAvatar alias={event.actor} />}
                  <Box fontWeight="bold">{event.actor}</Box>
                  <Box color="text-body-secondary" fontSize="body-s">
                    {formatTimestamp(event.timestamp)}
                  </Box>
                  <OriginBadge source={event.source} />
                  {canManage(event) && event.eventId !== editingId && (
                    <div className="comment-card__actions">
                      <ButtonDropdown
                        variant="inline-icon"
                        ariaLabel="Comment actions"
                        expandToViewport
                        items={[
                          { id: "edit", text: "Edit" },
                          { id: "delete", text: "Delete" },
                        ]}
                        onItemClick={({ detail }) => {
                          setError(null);
                          if (detail.id === "edit") {
                            setEditingId(event.eventId || null);
                          } else if (detail.id === "delete") {
                            setDeleteTarget(event);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="comment-card__body">{renderBody(event)}</div>
              </div>
            ))}
          </SpaceBetween>
        )}

        {nextToken && (
          <Box textAlign="center">
            <Button
              onClick={() => {
                loadOlder().catch(console.error);
              }}
              loading={loadingOlder}
            >
              Load older comments
            </Button>
          </Box>
        )}
      </SpaceBetween>

      {deleteTarget && (
        <Modal
          visible
          onDismiss={() => setDeleteTarget(null)}
          header="Delete comment"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleteComment.isLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  loading={deleteComment.isLoading}
                  onClick={() => {
                    confirmDelete().catch(console.error);
                  }}
                >
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="s">
            <Box>
              The comment disappears from this list. It stays in the audit log
              marked as deleted, with its text
              {commentAttachments(deleteTarget).length
                ? " and attachments"
                : ""}{" "}
              still readable there. This cannot be undone.
            </Box>
            {commentText(deleteTarget) && (
              <Box color="text-body-secondary">
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 160,
                    overflow: "auto",
                  }}
                >
                  {commentText(deleteTarget)}
                </div>
              </Box>
            )}
          </SpaceBetween>
        </Modal>
      )}
    </Container>
  );
};

export default InvestigationComments;
